<script lang="ts">
export default {
  name: 'KkAiPostModal',
}
</script>

<script lang="ts" setup>
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { KkModal, KkFormLabel, KkTextField, KkTextarea, KkButton, KkLoading } from '../../../../components/base'
import { KkInfo } from '../../../../components/ui'
import { actionEvent } from '../../../../modules/googleAnalytics'

export interface Props {
  isVisible: boolean
  level?: '1' | '2' | '3' | '4' | '5' | '6'
}

export interface Emits {
  (key: 'update:isVisible', value: Props['isVisible']): void
  (key: 'submit', value: string): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const store = useStore()
const { t } = useI18n()

const keywords = ref('')
const url = ref('')
const appeal = ref('')

const loading = ref(false)
const error = ref('')

const generateAiPost = async () => {
  loading.value = true
  actionEvent('AIPostSupport')
  try {
    const response = await store.dispatch('companiesStorePosts/generateAiPost', {
      keywords: keywords.value,
      url: url.value,
      appeal: appeal.value,
    })

    if (response.data?.status !== 'succeeded') {
      error.value = t(`pages.CompaniesStorePost.aiPost.errors.${response.data?.status}`)

      if (!error.value) {
        error.value = t('pages.CompaniesStorePost.aiPost.errors.others')
      }
    } else {
      emits('submit', response.data.text)
      error.value = ''
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <KkModal :is-visible="props.isVisible" @change="emits('update:isVisible', false)">
    <header :class="$style.header">
      <component :is="`h${props.level}`" :class="$style.title">{{ $t('pages.CompaniesStorePost.aiPost.title') }}</component>
    </header>
    <div :class="$style.content">
      <template v-if="!loading">
        <KkInfo v-if="error" icon="exclamation" alert>{{ error }}</KkInfo>
        <div :class="$style.field">
          <KkFormLabel :text="`${$t('pages.CompaniesStorePost.aiPost.keywords')}${$t('labels.requiredLabel')}`" html-for="keywords" :class="$style.label" />
          <KkTextField id="keywords" v-model="keywords" :placeholder="$t('pages.CompaniesStorePost.aiPost.keywordsPlaceholder')" :maxlength="300" />
        </div>

        <div :class="$style.field">
          <KkFormLabel :text="$t('pages.CompaniesStorePost.aiPost.referenceUrl')" html-for="url" :class="$style.label" />
          <KkTextField id="url" v-model="url" :placeholder="$t('pages.CompaniesStorePost.aiPost.referenceUrlPlaceholder')" type="url" :maxlength="2000" />
        </div>

        <div :class="$style.field">
          <KkFormLabel :text="$t('pages.CompaniesStorePost.aiPost.appeal')" html-for="appeal" :class="$style.label" />
          <KkTextarea id="appeal" v-model="appeal" :placeholder="$t('pages.CompaniesStorePost.aiPost.appealPlaceholder')" :maxlength="2000" />
        </div>
      </template>
      <KkLoading v-else min-height="200px" />
    </div>

    <div :class="$style.footer">
      <div :class="$style.buttons">
        <KkButton :text="$t('labels.cancel')" color="secondary" @click="emits('update:isVisible', false)" />
        <KkButton :text="$t('pages.CompaniesStorePost.aiPost.request')" :disabled="!keywords || loading" @click="generateAiPost" />
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" module>
.header {
  border-bottom: 1px solid #E4E7EB;
  padding: 24px 32px;
  line-height: 1;
}
.title {
  font-weight: bold;
  font-size: 16px;
}
.content {
  max-height: 50vh;
  padding: 20px 32px 32px;
  overflow-y: auto;
}
.field {
  &:not(:first-child) {
    margin-top: 32px;
  }
}
.label {
  display: inline-block;
  margin-bottom: 12px;
}
.footer {
  border-top: 1px solid #E4E7EB;
  padding: 20px 32px;
}
.buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0 12px;
}
</style>
